import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import DashboardLayout from "../layout/index";
import { Search, Eye, Building2, CheckCircle, XCircle, RotateCcw, ChevronDown, X } from "lucide-react";
import { buildLogoUrl, extractSignedUrlFromResponse } from "./shared/onboardingUtils";

const getHospitalName = (hospital) =>
  hospital?.firstName || hospital?.name || hospital?.hospitalName || "-";

const getHospitalId = (hospital) =>
  hospital?.hospitalId || hospital?.userId || hospital?.id;

const BUSINESS_STATUS = {
  PENDING: 0,
  APPROVED: 1,
  RESUBMIT: 2,
  REJECTED: 3,
};

const getBusinessStatus = (hospital) => {
  const value = hospital?.businessApprovalStatus ?? hospital?.businessStatus;
  return value === undefined || value === null ? BUSINESS_STATUS.PENDING : Number(value);
};

const getFinanceStatus = (hospital) => {
  const value = hospital?.financeApprovalStatus ?? hospital?.financeStatus;
  return value === undefined || value === null ? BUSINESS_STATUS.PENDING : Number(value);
};

// Combines business + finance approval into one label: business approval is only the
// first step, the hospital isn't fully approved until finance signs off too.
const getDisplayStatusLabel = (hospital) => {
  const businessStatus = getBusinessStatus(hospital);
  if (businessStatus === BUSINESS_STATUS.RESUBMIT) return "Resubmit Requested";
  if (businessStatus === BUSINESS_STATUS.REJECTED) return "Rejected";
  if (businessStatus === BUSINESS_STATUS.PENDING) return "Pending";

  const financeStatus = getFinanceStatus(hospital);
  switch (financeStatus) {
    case BUSINESS_STATUS.APPROVED:
      return "Approved";
    case BUSINESS_STATUS.RESUBMIT:
      return "Finance Resubmit Requested";
    case BUSINESS_STATUS.REJECTED:
      return "Finance Rejected";
    case BUSINESS_STATUS.PENDING:
    default:
      return "Waiting for Finance Approval";
  }
};

// Filter dropdown options; each maps to the businessStatus/financeStatus query params
// the backend expects (both exact-match). "Waiting for Finance Approval" means business
// has already approved and it's now sitting with finance.
const STATUS_FILTER_OPTIONS = [
  { value: "ALL", label: "All Statuses", params: {} },
  { value: "PENDING", label: "Pending", params: { businessStatus: 0 } },
  { value: "RESUBMIT", label: "Resubmit Requested", params: { businessStatus: 2 } },
  { value: "REJECTED", label: "Rejected", params: { businessStatus: 3 } },
  {
    value: "WAITING_FINANCE",
    label: "Waiting for Finance Approval",
    params: { businessStatus: 1, financeStatus: 0 },
  },
  {
    value: "FINANCE_RESUBMIT",
    label: "Finance Resubmit Requested",
    params: { businessStatus: 1, financeStatus: 2 },
  },
  {
    value: "FINANCE_REJECTED",
    label: "Finance Rejected",
    params: { businessStatus: 1, financeStatus: 3 },
  },
  { value: "APPROVED", label: "Approved", params: { businessStatus: 1, financeStatus: 1 } },
];

// Finance only ever reviews hospitals business has already approved, so its dropdown
// (and every option in it, including "All") is scoped to businessStatus=1 — a
// business-pending/resubmit/rejected hospital must never be reachable from finance.
const FINANCE_STATUS_FILTER_OPTIONS = [
  { value: "ALL", label: "All Statuses", params: { businessStatus: 1 } },
  ...STATUS_FILTER_OPTIONS.filter((o) =>
    ["WAITING_FINANCE", "FINANCE_RESUBMIT", "FINANCE_REJECTED", "APPROVED"].includes(o.value)
  ),
];

const getDisplayStatusStyle = (hospital) => {
  const businessStatus = getBusinessStatus(hospital);
  if (businessStatus === BUSINESS_STATUS.RESUBMIT) return "bg-amber-100 text-amber-700";
  if (businessStatus === BUSINESS_STATUS.REJECTED) return "bg-red-100 text-red-700";
  if (businessStatus === BUSINESS_STATUS.PENDING) return "bg-gray-100 text-gray-700";

  const financeStatus = getFinanceStatus(hospital);
  switch (financeStatus) {
    case BUSINESS_STATUS.APPROVED:
      return "bg-emerald-100 text-emerald-700";
    case BUSINESS_STATUS.RESUBMIT:
      return "bg-amber-100 text-amber-700";
    case BUSINESS_STATUS.REJECTED:
      return "bg-red-100 text-red-700";
    case BUSINESS_STATUS.PENDING:
    default:
      return "bg-sky-100 text-sky-700";
  }
};

export default function HospitalList() {
  const API_BASE = process.env.REACT_APP_API_URL;
  const role = localStorage.getItem("role") || "";
  const roleList = role.split(",").map((r) => r.trim()).filter(Boolean);
  const isFinanceRole = roleList.includes("FINANCE");
  const isBusinessRole = roleList.includes("BUSINESS");
  const canUpdateStatus = isFinanceRole || isBusinessRole;
  const activeStatusFilterOptions = isFinanceRole ? FINANCE_STATUS_FILTER_OPTIONS : STATUS_FILTER_OPTIONS;

  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(
    isFinanceRole ? "WAITING_FINANCE" : isBusinessRole ? "PENDING" : "ALL"
  );
  const searchDebounceRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [remarkView, setRemarkView] = useState(null);
  const [viewHospital, setViewHospital] = useState(null);
  const [agreementPreviewUrl, setAgreementPreviewUrl] = useState("");
  const [agreementPreviewLoading, setAgreementPreviewLoading] = useState(false);

  useEffect(() => {
    setAgreementPreviewUrl("");
    const fileName = viewHospital?.agreementAttachmentUrl;
    if (!fileName) return;

    let cancelled = false;
    const loadAgreementPreview = async () => {
      setAgreementPreviewLoading(true);
      try {
        const token = localStorage.getItem("auth_token");
        const tenantId = viewHospital?.tenantId;
        if (!token || !tenantId) return;

        const res = await fetch(buildLogoUrl(fileName, API_BASE), {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, tenantId, Accept: "*/*" },
          redirect: "follow",
        });
        if (!res.ok || cancelled) return;

        const contentType = res.headers.get("content-type") || "";
        if (contentType.includes("application/json")) {
          const data = await res.json().catch(() => null);
          const signedUrl = extractSignedUrlFromResponse(data);
          if (!cancelled && signedUrl) setAgreementPreviewUrl(signedUrl);
          return;
        }

        const blob = await res.blob();
        if (!cancelled && blob.size > 0) {
          setAgreementPreviewUrl(URL.createObjectURL(blob));
        }
      } catch (error) {
        console.error("Failed to load agreement attachment preview:", error);
      } finally {
        if (!cancelled) setAgreementPreviewLoading(false);
      }
    };

    loadAgreementPreview();
    return () => {
      cancelled = true;
    };
  }, [viewHospital, API_BASE]);
  const [statusConfirm, setStatusConfirm] = useState(null);
  const [statusRemark, setStatusRemark] = useState("");
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const actionMenuTriggerRef = useRef(null);
  const actionMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionMenuTriggerRef.current && actionMenuTriggerRef.current.contains(e.target)) return;
      if (actionMenuRef.current && actionMenuRef.current.contains(e.target)) return;
      setOpenActionMenuId(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchInput]);

  const fetchHospitals = async () => {
    if (!API_BASE) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("search", debouncedSearch);
      const filterParams = activeStatusFilterOptions.find((o) => o.value === statusFilter)?.params || {};
      Object.entries(filterParams).forEach(([key, value]) => params.set(key, String(value)));
      params.set("page", String(currentPage - 1));
      params.set("size", String(pageSize));

      const baseUrl = API_BASE.endsWith("/") ? API_BASE : `${API_BASE}/`;
      const res = await fetch(`${baseUrl}hospital/list?${params.toString()}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();
      const items = Array.isArray(data) ? data : data?.content || data?.items || [];
      setHospitals(items || []);
      setTotalElements(data?.totalElements ?? items.length ?? 0);
    } catch (error) {
      console.error("Failed to fetch hospital list:", error);
      toast.error("Failed to fetch hospital list");
      setHospitals([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHospitals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE, debouncedSearch, statusFilter, currentPage, pageSize]);

  const openStatusConfirm = (hospital, status, label, pastLabel) => {
    setStatusRemark("");
    setStatusConfirm({ hospital, status, label, pastLabel });
  };

  const updateBusinessStatus = async ({ hospital, status, label, pastLabel, remark }) => {
    const hospitalId = getHospitalId(hospital);
    const tenantId = hospital?.tenantId;
    // FINANCE reviews after BUSINESS has already approved, so it goes through its own
    // endpoint/status fields (financeStatus/financeRemark) rather than business-status.
    const endpoint = isFinanceRole ? "hospital/finance-status" : "hospital/business-status";

    setStatusUpdatingId(hospitalId);
    try {
      const token = localStorage.getItem("auth_token");
      const baseUrl = API_BASE.endsWith("/") ? API_BASE : `${API_BASE}/`;

      const callStatusApi = (includeTenantId) =>
        fetch(`${baseUrl}${endpoint}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(includeTenantId && tenantId ? { tenantId } : {}),
          },
          body: JSON.stringify({ hospitalId, status, remark }),
        });

      const res = await callStatusApi(true);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const errorMsg = data?.error || data?.message || `Failed to ${label.toLowerCase()} hospital`;
        toast.error(errorMsg);
        return;
      }

      try {
        await callStatusApi(false);
      } catch (secondError) {
        console.error(`Failed to update status via ${endpoint} (without tenantId):`, secondError);
      }

      toast.success(data?.message || `Hospital ${pastLabel} successfully`);
      setStatusConfirm(null);
      setStatusRemark("");
      fetchHospitals();
    } catch (error) {
      console.error("Failed to update hospital status:", error);
      toast.error(`Failed to ${label.toLowerCase()} hospital`);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleApprove = (hospital) => {
    updateBusinessStatus({
      hospital,
      status: BUSINESS_STATUS.APPROVED,
      label: "Approve",
      pastLabel: "approved",
      remark: "",
    });
  };

  const handleStatusConfirm = async () => {
    if (!statusConfirm) return;
    const { hospital, status, label, pastLabel } = statusConfirm;
    const remark = statusRemark.trim();
    const remarkRequired = status === BUSINESS_STATUS.RESUBMIT || status === BUSINESS_STATUS.REJECTED;
    if (remarkRequired && !remark) {
      toast.error("Please enter a remark");
      return;
    }

    await updateBusinessStatus({ hospital, status, label, pastLabel, remark });
  };

  const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));
  const startEntry = totalElements === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, totalElements);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <DashboardLayout>
      <div className="mt-8 px-4 sm:px-6 min-w-0 w-full max-w-full">
    

        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative max-w-sm w-full">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by hospital name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] focus:border-[#3cba84]"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full sm:w-64 px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] focus:border-[#3cba84] text-sm text-[#4B5563]"
            >
              {activeStatusFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 min-w-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-sky-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563] tracking-wider">
                    Hospital Name
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563] tracking-wider">
                    Mobile
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563] tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-[#4B5563] tracking-wider">
                    City / State
                  </th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-[#4B5563] tracking-wider">
                    Business Status
                  </th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-[#4B5563] tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      Loading hospitals...
                    </td>
                  </tr>
                ) : hospitals.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      No hospitals found
                    </td>
                  </tr>
                ) : (
                  hospitals.map((hospital, index) => {
                    const hospitalId = getHospitalId(hospital);
                    const businessStatus = getBusinessStatus(hospital);
                    const remark =
                      businessStatus === BUSINESS_STATUS.APPROVED
                        ? hospital?.financeRemark || hospital?.businessRemark
                        : hospital?.businessRemark;
                    const alreadyActed = isFinanceRole
                      ? getFinanceStatus(hospital) !== BUSINESS_STATUS.PENDING
                      : getBusinessStatus(hospital) !== BUSINESS_STATUS.PENDING;
                    return (
                    <tr key={hospitalId || index} className={index % 2 === 0 ? "bg-white" : "bg-[#FDFDFD]"}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-[#4B5563]">
                          <div className="h-6 w-6 rounded-lg bg-teal-600 flex items-center justify-center shadow flex-shrink-0">
                            <Building2 size={13} className="text-white" />
                          </div>
                          <span>{getHospitalName(hospital)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#4B5563]">
                        {hospital?.mobile || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#4B5563]">{hospital?.email || "-"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#4B5563]">
                        {[hospital?.city, hospital?.state].filter(Boolean).join(", ") || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span
                          className={`inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-full ${getDisplayStatusStyle(
                            hospital
                          )}`}
                        >
                          {getDisplayStatusLabel(hospital)}
                          {remark && (
                            <button
                              type="button"
                              onClick={() => setRemarkView({ hospital, remark })}
                              title="View remark"
                              className="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-black/10 transition-colors"
                            >
                              <Eye size={12} />
                            </button>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex flex-nowrap gap-2 justify-center items-center">
                          <button
                            type="button"
                            onClick={() => setViewHospital(hospital)}
                            className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-xl text-[12px] bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition-colors"
                          >
                            <Eye size={14} />
                            View
                          </button>
                          {canUpdateStatus && (
                            <div
                              className="relative"
                              ref={openActionMenuId === hospitalId ? actionMenuTriggerRef : null}
                            >
                              <button
                                type="button"
                                onClick={(e) => {
                                  if (openActionMenuId === hospitalId) {
                                    setOpenActionMenuId(null);
                                    setActionMenuPosition(null);
                                    return;
                                  }
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setActionMenuPosition({
                                    top: rect.bottom + 4,
                                    left: Math.max(8, rect.right - 176),
                                  });
                                  setOpenActionMenuId(hospitalId);
                                }}
                                disabled={statusUpdatingId === hospitalId || alreadyActed}
                                title={alreadyActed ? "You have already taken action on this hospital" : undefined}
                                className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-xl text-[12px] bg-gray-50 text-gray-700 border border-gray-300 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Actions
                                <ChevronDown size={14} />
                              </button>
                              {openActionMenuId === hospitalId &&
                                actionMenuPosition &&
                                createPortal(
                                  <div
                                    ref={actionMenuRef}
                                    style={{ top: actionMenuPosition.top, left: actionMenuPosition.left }}
                                    className="fixed z-50 w-44 rounded-xl border border-gray-200 bg-white shadow-lg py-1"
                                  >
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenActionMenuId(null);
                                        handleApprove(hospital);
                                      }}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-emerald-600 hover:bg-emerald-50 transition-colors"
                                    >
                                      <CheckCircle size={14} />
                                      Approve
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenActionMenuId(null);
                                        openStatusConfirm(hospital, BUSINESS_STATUS.RESUBMIT, "Resubmit", "sent back for resubmission");
                                      }}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-amber-700 hover:bg-amber-50 transition-colors"
                                    >
                                      <RotateCcw size={14} />
                                      Resubmit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenActionMenuId(null);
                                        openStatusConfirm(hospital, BUSINESS_STATUS.REJECTED, "Reject", "rejected");
                                      }}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-red-600 hover:bg-red-50 transition-colors"
                                    >
                                      <XCircle size={14} />
                                      Reject
                                    </button>
                                  </div>,
                                  document.body
                                )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 0 && (
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-white rounded-b-lg">
              <div className="flex sm:hidden items-center justify-between gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`text-sm px-3 py-1.5 rounded-lg border ${currentPage === 1 ? "text-[#9CA3AF] border-gray-200 cursor-not-allowed" : "text-[#4B5563] border-gray-300 hover:bg-gray-50"}`}
                >
                  &lt; Prev
                </button>
                <div className="text-sm text-[#6B7280]">
                  Page {currentPage} of {totalPages}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`text-sm px-3 py-1.5 rounded-lg border ${currentPage === totalPages ? "text-[#9CA3AF] border-gray-200 cursor-not-allowed" : "text-[#4B5563] border-gray-300 hover:bg-gray-50"}`}
                >
                  Next &gt;
                </button>
              </div>
              <div className="mt-2 text-center text-xs text-[#6B7280] sm:hidden">
                Showing {startEntry} to {endEntry} of {totalElements} entries
              </div>

              <div className="hidden sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`text-sm ${currentPage === 1 ? "text-[#6B7280] cursor-not-allowed" : "text-[#6B7280] hover:text-[#4B5563]"}`}
                  >
                    &lt; Prev
                  </button>
                  <div className="flex items-center gap-1">
                    {getPageNumbers().map((pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 text-sm ${
                          currentPage === pageNum
                            ? "border border-[#4B5563] text-[#4B5563] bg-white"
                            : "text-[#6B7280] hover:text-[#4B5563]"
                        }`}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`text-sm ${currentPage === totalPages ? "text-[#6B7280] cursor-not-allowed" : "text-[#6B7280] hover:text-[#4B5563]"}`}
                  >
                    Next &gt;
                  </button>
                </div>
                <div className="text-sm text-[#6B7280]">
                  Showing {startEntry} to {endEntry} of {totalElements} entries
                </div>
              </div>
            </div>
          )}
        </div>
      </div>


      {viewHospital && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          onClick={() => setViewHospital(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-1">
              <h3 className="text-lg font-semibold text-[#004276]">
                {getHospitalName(viewHospital)}
              </h3>
              <button
                type="button"
                onClick={() => setViewHospital(null)}
                aria-label="Close"
                className="shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">{viewHospital?.hospitalId}</p>

            {[
              {
                title: "Hospital details",
                fields: [
                  ["Email", viewHospital?.email],
                  ["Mobile", viewHospital?.mobile],
                  ["Category Type", viewHospital?.categoryType],
                  ["GST", viewHospital?.gst],
                  ["PAN", viewHospital?.pancard],
                  ["Rohini ID", viewHospital?.rohiniId],
                  ["Tenant ID", viewHospital?.tenantId],
                ],
              },
              {
                title: "Address",
                fields: [
                  ["Address", viewHospital?.address],
                  ["City", viewHospital?.city],
                  ["State", viewHospital?.state],
                  ["Country", viewHospital?.country],
                  ["Pincode", viewHospital?.pinCode],
                ],
              },
              {
                title: "Capacity & services",
                fields: [
                  ["Doctors empaneled", viewHospital?.numberOfDoctorsEmpaneled],
                  ["Specialists empaneled", viewHospital?.numberOfSpecialistsEmpaneled],
                  ["Different services (IPD)", viewHospital?.differentServicesIPD],
                  ["Hospital timings", viewHospital?.hospitalTimings],
                  ["Onboarding type", viewHospital?.onboardingType],
                  ["About hospital", viewHospital?.aboutHospital],
                ],
              },
              {
                title: "Commercial & commission details",
                fields: [
                  ["IPD commercial (cash)", viewHospital?.ipdCommercialCash],
                  ["IPD commercial (insurance)", viewHospital?.ipdCommercialInsurance],
                  ["IPD commercial (government)", viewHospital?.ipdCommercialGovernment],
                  ["Healthcare loan commercial", viewHospital?.healthcareLoanCommercial],
                  ["Payment reconciliation / credit days", viewHospital?.paymentReconciliationOrCreditDays],
                  ["Referral commission (%)", viewHospital?.referralCommissionPercentage],
                  ["Non-referral commission (%)", viewHospital?.nonReferralCommissionPercentage],
                  ["OPD commission (%)", viewHospital?.opdCommissionPercentage],
                ],
              },
              {
                title: "Agreement details",
                fields: [
                  ["Agreement date", viewHospital?.agreementDate],
                  ["Termination notice period", viewHospital?.terminationNoticePeriod],
                  ["Payment period", viewHospital?.paymentPeriod],
                ],
              },
              {
                title: "Bank details",
                fields: [
                  ["Bank name", viewHospital?.bankName],
                  ["Bank account name", viewHospital?.bankAccountName],
                  ["Bank account number", viewHospital?.bankAccountNumber],
                  ["Bank IFSC", viewHospital?.bankIfsc],
                  ["Bank location", viewHospital?.bankLocation],
                ],
              },
            ]
              .map((section) => ({
                ...section,
                fields: section.fields.filter(
                  ([, value]) => value !== undefined && value !== null && value !== ""
                ),
              }))
              .filter((section) => section.fields.length > 0)
              .map((section) => (
                <div key={section.title} className="mb-5">
                  <h4 className="text-sm font-semibold text-[#004276] mb-2">{section.title}</h4>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    {section.fields.map(([label, value]) => (
                      <div key={label}>
                        <dt className="text-gray-500">{label}</dt>
                        <dd className="text-[#334155] font-medium break-words">{String(value)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}

            {viewHospital?.agreementAttachmentUrl && (
              <div className="mb-5">
                <h4 className="text-sm font-semibold text-[#004276] mb-2">Agreement attachment</h4>
                {agreementPreviewLoading ? (
                  <p className="text-sm text-gray-500">Loading document...</p>
                ) : agreementPreviewUrl ? (
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <iframe
                      src={agreementPreviewUrl}
                      title="Agreement attachment"
                      className="w-full h-[420px]"
                    />
                    <div className="flex justify-end p-2 border-t border-gray-200 bg-gray-50">
                      <a
                        href={agreementPreviewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-medium text-[#004276] hover:underline"
                      >
                        Open in new tab
                      </a>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Unable to load document preview.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {remarkView?.hospital && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          onClick={() => setRemarkView(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-[#004276] mb-4">Remark</h3>
            <p className="text-sm text-gray-600 mb-4">
              {getHospitalName(remarkView.hospital)}
            </p>
            <p className="text-sm text-[#334155] whitespace-pre-wrap">{remarkView.remark}</p>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setRemarkView(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {statusConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-[#004276] mb-4">
              {statusConfirm.label} hospital
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to {statusConfirm.label.toLowerCase()} "{getHospitalName(statusConfirm.hospital)}"?
            </p>
            {statusConfirm.status !== BUSINESS_STATUS.APPROVED && (
              <>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="business-status-remark">
                  Remark *
                </label>
                <textarea
                  id="business-status-remark"
                  rows={3}
                  value={statusRemark}
                  onChange={(e) => setStatusRemark(e.target.value)}
                  placeholder="Enter reason (required)"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] focus:border-[#3cba84]"
                />
              </>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setStatusConfirm(null);
                  setStatusRemark("");
                }}
                disabled={statusUpdatingId === getHospitalId(statusConfirm.hospital)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleStatusConfirm}
                disabled={statusUpdatingId === getHospitalId(statusConfirm.hospital)}
                className={`px-5 py-2.5 bg-gradient-to-r from-[#004172] to-[#3cba84] text-white rounded-lg ${
                  statusUpdatingId === getHospitalId(statusConfirm.hospital) ? "opacity-70 cursor-not-allowed" : "hover:opacity-90"
                }`}
              >
                {statusUpdatingId === getHospitalId(statusConfirm.hospital) ? "Processing..." : `Confirm ${statusConfirm.label}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
